import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Box, Typography, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Card, Alert, FormControl, InputLabel, Select, LinearProgress
} from '@mui/material';
import Layout from '../components/Layout';
import api from '../services/api';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

// ─── Constants ───────────────────────────────────────────────────────────────
const TASK_STATUSES = ['not_started','in_progress','completed','delayed'];
const DEP_STATUSES  = ['pending','provided','ordered','delivered','na'];
const TYPES         = ['task','milestone','phase'];

const TASK_S = {
  not_started: { bg:'#f5f5f5', color:'#757575', label:'Not Started' },
  in_progress:  { bg:'#e3f2fd', color:'#1565c0', label:'In Progress' },
  completed:    { bg:'#e8f5e9', color:'#2e7d32', label:'Completed'   },
  delayed:      { bg:'#ffebee', color:'#c62828', label:'Delayed'     },
};
const DEP_S = {
  na:        { bg:'#f5f5f5', color:'#9e9e9e', label:'N/A'       },
  pending:   { bg:'#fff3e0', color:'#e65100', label:'Pending'   },
  provided:  { bg:'#e8f5e9', color:'#2e7d32', label:'Provided'  },
  ordered:   { bg:'#e3f2fd', color:'#1565c0', label:'Ordered'   },
  delivered: { bg:'#f3e5f5', color:'#6a1b9a', label:'Delivered' },
};

const BLANK_FORM = {
  title:'', description:'', type:'task', status:'not_started',
  start_date: new Date().toISOString().slice(0,10),
  end_date:   new Date(Date.now()+7*86400000).toISOString().slice(0,10),
  color:'#1a237e', progress:0,
};

const BLANK_OD = {
  title:'', description:'', schedule_item_id:'',
  needed_by:'', supplier:'', lead_time_days:'', order_start:'', status:'pending',
};

// ─── Standard Construction Tasks ─────────────────────────────────────────────
const STANDARD_TASKS = [
  { label: 'Gas Disconnect',               category: 'Site Prep',    type: 'milestone', color: '#b71c1c' },
  { label: 'Electric Disconnect',          category: 'Site Prep',    type: 'milestone', color: '#b71c1c' },
  { label: 'Temp Power Pole Permit',       category: 'Site Prep',    type: 'task',      color: '#e53935' },
  { label: 'Temp Power Pole Installation', category: 'Site Prep',    type: 'task',      color: '#e53935' },
  { label: 'Temp Power Pole Inspection',   category: 'Site Prep',    type: 'task',      color: '#e53935' },
  { label: 'Construction Fence',           category: 'Site Prep',    type: 'task',      color: '#e53935' },
  { label: 'Portable Toilet',              category: 'Site Prep',    type: 'task',      color: '#e53935' },
  { label: 'Straw Wattles / Erosion Control', category: 'Site Prep', type: 'task',      color: '#e53935' },
  { label: 'Demo Permit',                  category: 'Permits',      type: 'task',      color: '#f57c00' },
  { label: 'Building Permit',              category: 'Permits',      type: 'task',      color: '#f57c00' },
  { label: 'Grading Permit',               category: 'Permits',      type: 'task',      color: '#f57c00' },
  { label: 'Staking / Survey',             category: 'Survey',       type: 'task',      color: '#1565c0' },
  { label: 'Soil Compaction Check',        category: 'Survey',       type: 'task',      color: '#1565c0' },
  { label: 'Soil Export',                  category: 'Civil',        type: 'task',      color: '#1565c0' },
  { label: 'Demolition',                   category: 'Demolition',   type: 'phase',     color: '#6a1b9a' },
  { label: 'Trash Haul Away',              category: 'Demolition',   type: 'task',      color: '#6a1b9a' },
  { label: 'Foundation',                   category: 'Foundation',   type: 'phase',     color: '#795548' },
  { label: 'Form & Rebar',                 category: 'Foundation',   type: 'task',      color: '#795548' },
  { label: 'Concrete Pour',                category: 'Foundation',   type: 'task',      color: '#795548' },
  { label: 'Drainage at Slab',             category: 'Foundation',   type: 'task',      color: '#795548' },
  { label: 'Foundation Inspection',        category: 'Foundation',   type: 'milestone', color: '#795548' },
  { label: 'Rat Slab',                     category: 'Foundation',   type: 'task',      color: '#795548' },
  { label: 'Subfloor Framing',             category: 'Framing',      type: 'task',      color: '#2e7d32' },
  { label: '1st Floor Framing',            category: 'Framing',      type: 'phase',     color: '#2e7d32' },
  { label: '2nd Floor Joists',             category: 'Framing',      type: 'task',      color: '#2e7d32' },
  { label: '2nd Floor Wall Framing',       category: 'Framing',      type: 'task',      color: '#2e7d32' },
  { label: 'Roof Framing',                 category: 'Framing',      type: 'task',      color: '#2e7d32' },
  { label: 'Master Deck',                  category: 'Framing',      type: 'task',      color: '#2e7d32' },
  { label: 'Balcony / Roof Extension',     category: 'Framing',      type: 'task',      color: '#2e7d32' },
  { label: 'Waterproofing for Subfloor',   category: 'Framing',      type: 'task',      color: '#2e7d32' },
  { label: 'Framing Inspection',           category: 'Framing',      type: 'milestone', color: '#2e7d32' },
  { label: 'Subfloor Plumbing & HVAC',     category: 'Rough MEP',    type: 'task',      color: '#00838f' },
  { label: 'Rough Electrical',             category: 'Rough MEP',    type: 'task',      color: '#00838f' },
  { label: 'Rough Plumbing',               category: 'Rough MEP',    type: 'task',      color: '#00838f' },
  { label: 'Rough HVAC',                   category: 'Rough MEP',    type: 'task',      color: '#00838f' },
  { label: 'Fire Sprinkler System',        category: 'Rough MEP',    type: 'task',      color: '#c62828' },
  { label: 'Rough MEP Inspection',         category: 'Rough MEP',    type: 'milestone', color: '#00838f' },
  { label: 'Exterior Lath & Tyvek',        category: 'Exterior',     type: 'task',      color: '#37474f' },
  { label: 'Scratch Coat Stucco',          category: 'Exterior',     type: 'task',      color: '#37474f' },
  { label: 'Brown Coat Stucco',            category: 'Exterior',     type: 'task',      color: '#37474f' },
  { label: 'Finish Coat Stucco',           category: 'Exterior',     type: 'task',      color: '#37474f' },
  { label: 'Siding Installation',          category: 'Exterior',     type: 'task',      color: '#37474f' },
  { label: 'Roofing',                      category: 'Exterior',     type: 'task',      color: '#37474f' },
  { label: 'Garage Door Installation',     category: 'Exterior',     type: 'task',      color: '#37474f' },
  { label: 'Exterior Paint',               category: 'Exterior',     type: 'task',      color: '#37474f' },
  { label: 'Subfloor Insulation',          category: 'Insulation',   type: 'task',      color: '#4527a0' },
  { label: 'Insulation',                   category: 'Insulation',   type: 'task',      color: '#4527a0' },
  { label: 'Insulation Inspection',        category: 'Insulation',   type: 'milestone', color: '#4527a0' },
  { label: 'Drywall',                      category: 'Drywall',      type: 'task',      color: '#1565c0' },
  { label: 'Drywall Patch',                category: 'Drywall',      type: 'task',      color: '#1565c0' },
  { label: 'Drywall Inspection',           category: 'Drywall',      type: 'milestone', color: '#1565c0' },
  { label: 'Hardwood Floor',               category: 'Finishes',     type: 'task',      color: '#795548' },
  { label: 'Tile Work',                    category: 'Finishes',     type: 'task',      color: '#ef6c00' },
  { label: 'Cabinet Installation',         category: 'Finishes',     type: 'task',      color: '#5c6bc0' },
  { label: 'Countertop Installation',      category: 'Finishes',     type: 'task',      color: '#5c6bc0' },
  { label: 'Vanity Installation',          category: 'Finishes',     type: 'task',      color: '#5c6bc0' },
  { label: 'Baseboards & Trim',            category: 'Finishes',     type: 'task',      color: '#795548' },
  { label: 'Painting Phase I',             category: 'Finishes',     type: 'task',      color: '#e91e63' },
  { label: 'Painting Phase II',            category: 'Finishes',     type: 'task',      color: '#e91e63' },
  { label: 'Mirror & Glass / Shower Enclosure', category: 'Finishes', type: 'task',    color: '#5c6bc0' },
  { label: 'Plumbing Fixtures',            category: 'MEP Finish',   type: 'task',      color: '#00838f' },
  { label: 'Lighting Fixtures Phase I',    category: 'MEP Finish',   type: 'task',      color: '#f9a825' },
  { label: 'Lighting Fixtures Phase II',   category: 'MEP Finish',   type: 'task',      color: '#f9a825' },
  { label: 'HVAC Finish',                  category: 'MEP Finish',   type: 'task',      color: '#00838f' },
  { label: 'Appliance Installation',       category: 'MEP Finish',   type: 'task',      color: '#546e7a' },
  { label: 'Solar Installation',           category: 'MEP Finish',   type: 'task',      color: '#f9a825' },
  { label: 'Communication (Internet/Speakers)', category: 'MEP Finish', type: 'task',  color: '#546e7a' },
  { label: 'Landscape Phase I',            category: 'Landscape',    type: 'task',      color: '#388e3c' },
  { label: 'Landscape Phase II',           category: 'Landscape',    type: 'task',      color: '#388e3c' },
  { label: 'Driveway & Walkpath',          category: 'Landscape',    type: 'task',      color: '#388e3c' },
  { label: 'Fencing',                      category: 'Landscape',    type: 'task',      color: '#388e3c' },
  { label: 'Final Inspection',             category: 'Closeout',     type: 'milestone', color: '#c62828' },
  { label: 'Certificate of Occupancy',     category: 'Closeout',     type: 'milestone', color: '#1b5e20' },
  { label: 'House Cleaning',               category: 'Closeout',     type: 'task',      color: '#546e7a' },
  { label: 'Custom Task',                  category: 'Custom',       type: 'task',      color: '#1a237e' },
];

const TASK_CATEGORIES = [...new Set(STANDARD_TASKS.map(t => t.category))];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const cleanDate = d => d ? String(d).slice(0,10) : '';

const fmtDate = d => {
  if (!d) return '—';
  const [y,m,day] = cleanDate(d).split('-');
  if (!y||!m||!day) return '—';
  const MON=['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
  return `${MON[+m-1]}-${day}-${y.slice(2)}`;
};
const days = (s,e) => {
  if (!s||!e) return '—';
  return Math.ceil((new Date(cleanDate(e))-new Date(cleanDate(s)))/86400000)+1+'d';
};

// ─── Sub-components ──────────────────────────────────────────────────────────
const SH = ({children, span, style={}}) => (
  <th colSpan={span} style={{padding:'9px 12px', textAlign:'center', fontWeight:700,
    fontSize:11, letterSpacing:1.5, ...style}}>{children}</th>
);
const CH = ({children, center=false, style={}}) => (
  <th style={{padding:'8px 10px', textAlign:center?'center':'left', fontWeight:600,
    fontSize:11, whiteSpace:'nowrap', ...style}}>{children}</th>
);
const TD = ({children, center=false, style={}}) => (
  <td style={{padding:'8px 10px', textAlign:center?'center':'left',
    fontSize:12, verticalAlign:'middle', ...style}}>{children}</td>
);

const StatusSelect = ({value, options, map, onChange}) => (
  <Select size="small" value={value} onChange={e=>onChange(e.target.value)}
    sx={{fontSize:11, height:26, minWidth:90, bgcolor: map[value]?.bg||'#f5f5f5',
      color: map[value]?.color||'#333', fontWeight:700,
      '.MuiOutlinedInput-notchedOutline':{border:'none'},
      '.MuiSelect-select':{py:0.4, px:1}}}>
    {options.map(s=><MenuItem key={s} value={s} sx={{fontSize:11}}>{map[s]?.label||s}</MenuItem>)}
  </Select>
);

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Schedule() {
  const { user } = useAuth();
  const [projects,     setProjects]     = useState([]);
  const [projectId,    setProjectId]    = useState('');
  const [items,        setItems]        = useState([]);
  const [ownerDeps,    setOwnerDeps]    = useState([]);

  const [dialog,       setDialog]       = useState(false);
  const [editItem,     setEditItem]     = useState(null);
  const [form,         setForm]         = useState(BLANK_FORM);
  const [error,        setError]        = useState('');
  const [saving,       setSaving]       = useState(false);
  const [taskCategory, setTaskCategory] = useState('');

  const [odDialog,     setOdDialog]     = useState(false);
  const [odEdit,       setOdEdit]       = useState(null);
  const [odForm,       setOdForm]       = useState(BLANK_OD);
  const [odSaving,     setOdSaving]     = useState(false);
  const [odError,      setOdError]      = useState('');
  const [projectMembers, setProjectMembers] = useState([]);
  const [odPersonTab, setOdPersonTab] = useState('all');
  const [odTabInitialized, setOdTabInitialized] = useState(false);
  const [selectedOdIds, setSelectedOdIds] = useState(new Set());
  const [reminderSnack, setReminderSnack] = useState(null);

  const [panel,        setPanel]        = useState(null);
  const [panelOdEdit,  setPanelOdEdit]  = useState(false);

  const [statusFilter, setStatusFilter] = useState('not_completed');
  const [typeFilter,   setTypeFilter]   = useState('all');

  useEffect(()=>{
    api.get('/projects').then(r=>{
      const p = Array.isArray(r.data)?r.data:[];
      setProjects(p);
      if(p.length) setProjectId(p[0].id);
    }).catch(()=>{});
  },[]);

  useEffect(()=>{
    if(!projectId) return;
    api.get(`/projects/${projectId}/schedule`)
      .then(r=>setItems(Array.isArray(r.data)?r.data:[]))
      .catch(()=>setItems([]));
    api.get(`/projects/${projectId}/owner-dependencies`)
      .then(r=>setOwnerDeps(Array.isArray(r.data)?r.data:[]))
      .catch(()=>setOwnerDeps([]));
    api.get(`/projects/${projectId}`)
      .then(r=>{
        const p = r.data;
        const members = [];
        if(p.homeowner_id) members.push({id:p.homeowner_id, name:`${p.homeowner_first||''} ${p.homeowner_last||''}`.trim()||p.homeowner_email||'Homeowner', role:'homeowner'});
        if(p.gc_id) members.push({id:p.gc_id, name:`${p.gc_first||''} ${p.gc_last||''}`.trim()||p.gc_email||'General Contractor', role:'gc'});
        setProjectMembers(members);
      })
      .catch(()=>setProjectMembers([]));
  },[projectId]);

  // Default to logged-in user's tab on first load
  useEffect(() => {
    if (odTabInitialized || !ownerDeps.length || !user) return;
    const hasMyTasks = ownerDeps.some(od => od.assigned_to === user.id);
    setOdPersonTab(hasMyTasks ? user.id : 'all');
    setOdTabInitialized(true);
  }, [ownerDeps, user, odTabInitialized]);

  // Reset tab initialized state when project changes
  useEffect(() => {
    setOdTabInitialized(false);
    setOdPersonTab('all');
  }, [projectId]);

  const openCreate = () => { setEditItem(null); setForm(BLANK_FORM); setError(''); setTaskCategory(''); setDialog(true); };
  const openEdit   = item => {
    setEditItem(item);
    setForm({
      title:item.title, description:item.description||'', type:item.type,
      status:item.status, start_date:cleanDate(item.start_date), end_date:cleanDate(item.end_date),
      color:item.color||'#1a237e', progress:item.progress||0,
    });
    setError(''); setTaskCategory(''); setDialog(true);
  };

  const save = async () => {
    if(!form.title||!form.start_date||!form.end_date){ setError('Title and dates are required'); return; }
    setSaving(true); setError('');
    try {
      if(editItem){
        const r = await api.put(`/projects/${projectId}/schedule/${editItem.id}`, form);
        setItems(items.map(i=>i.id===editItem.id?r.data:i));
      } else {
        const r = await api.post(`/projects/${projectId}/schedule`, form);
        setItems([...items, r.data]);
      }
      setDialog(false);
    } catch(e){ setError(e.response?.data?.error||'Failed to save'); }
    finally{ setSaving(false); }
  };

  const remove = async id => {
    if(!window.confirm('Delete this task?')) return;
    await api.delete(`/projects/${projectId}/schedule/${id}`);
    setItems(items.filter(i=>i.id!==id));
    setOwnerDeps(ownerDeps.filter(od=>od.schedule_item_id!==id));
  };

  const patchItem = async (item, patch) => {
    const r = await api.put(`/projects/${projectId}/schedule/${item.id}`, patch);
    setItems(items.map(i=>i.id===item.id?r.data:i));
  };

  const openOdCreate = (scheduleItemId='') => {
    setOdEdit(null);
    setOdForm({...BLANK_OD, schedule_item_id: scheduleItemId});
    setOdError('');
    setOdDialog(true);
  };

  const openOdEdit = od => {
    setOdEdit(od);
    setOdForm({
      title: od.title||'',
      description: od.description||'',
      schedule_item_id: od.schedule_item_id||'',
      needed_by: cleanDate(od.needed_by),
      supplier: od.supplier||'',
      lead_time_days: od.lead_time_days||'',
      order_start: cleanDate(od.order_start),
      status: od.status||'pending',
      assigned_to: od.assigned_to||'',
    });
    setOdError('');
    setOdDialog(true);
  };

  const saveOd = async () => {
    if(!odForm.title){ setOdError('Title is required'); return; }
    setOdSaving(true); setOdError('');
    try {
      const payload = {
        ...odForm,
        schedule_item_id: odForm.schedule_item_id || null,
        needed_by: odForm.needed_by || null,
        order_start: odForm.order_start || null,
        lead_time_days: odForm.lead_time_days || null,
      };
      if(odEdit){
        const r = await api.put(`/projects/${projectId}/owner-dependencies/${odEdit.id}`, payload);
        setOwnerDeps(ownerDeps.map(od=>od.id===odEdit.id?r.data:od));
      } else {
        const r = await api.post(`/projects/${projectId}/owner-dependencies`, payload);
        setOwnerDeps([...ownerDeps, r.data]);
      }
      setOdDialog(false);
    } catch(e){ setOdError(e.response?.data?.error||'Failed to save'); }
    finally{ setOdSaving(false); }
  };

  const removeOd = async (id, e) => {
    if(e) e.stopPropagation();
    if(!window.confirm('Delete this owner dependency?')) return;
    await api.delete(`/projects/${projectId}/owner-dependencies/${id}`);
    setOwnerDeps(ownerDeps.filter(od=>od.id!==id));
  };

  const patchOdStatus = async (od, status, e) => {
    if(e) e.stopPropagation();
    const r = await api.put(`/projects/${projectId}/owner-dependencies/${od.id}`, { status });
    setOwnerDeps(ownerDeps.map(o=>o.id===od.id?r.data:o));
  };

  const filtered = items.filter(i=>{
    if(statusFilter==='not_completed' && i.status==='completed') return false;
    else if(statusFilter!=='all' && statusFilter!=='not_completed' && i.status!==statusFilter) return false;
    if(typeFilter!=='all'   && i.type!==typeFilter)     return false;
    return true;
  });

  const stats = {
    total:      items.length,
    completed:  items.filter(i=>i.status==='completed').length,
    inProgress: items.filter(i=>i.status==='in_progress').length,
    notStarted: items.filter(i=>i.status==='not_started').length,
    delayed:    items.filter(i=>i.status==='delayed').length,
    progress:   items.length ? Math.round(items.reduce((s,i)=>s+(i.progress||0),0)/items.length) : 0,
  };

  const isOverdue = od =>
    od.needed_by && new Date(cleanDate(od.needed_by)) < new Date() &&
    !['provided','delivered'].includes(od.status);

  // Unique assigned persons for per-person tabs (enriched with role from projectMembers)
  const ROLE_LABELS = { homeowner: 'Owner', gc: 'GC', general_contractor: 'GC', subcontractor: 'Sub' };
  const odPersons = Object.values(
    ownerDeps.reduce((acc, od) => {
      const key = od.assigned_to || '__unassigned__';
      if (!acc[key]) {
        const member = projectMembers.find(m => m.id === od.assigned_to);
        acc[key] = {
          id: od.assigned_to || null,
          tabKey: key,
          name: od.assigned_to
            ? (`${od.assignee_first||''} ${od.assignee_last||''}`.trim() || od.assignee_email || 'Unknown')
            : 'Unassigned',
          roleLabel: member ? (ROLE_LABELS[member.role] || member.role) : '',
        };
      }
      return acc;
    }, {})
  );

  // Tasks filtered by selected person tab
  const filteredOdDeps = odPersonTab === 'all'
    ? ownerDeps
    : odPersonTab === '__unassigned__'
      ? ownerDeps.filter(od => !od.assigned_to)
      : ownerDeps.filter(od => od.assigned_to === odPersonTab);

  const odStats = {
    overdue: filteredOdDeps.filter(isOverdue).length,
    pending: filteredOdDeps.filter(od=>['pending','na'].includes(od.status)).length,
    ordered: filteredOdDeps.filter(od=>od.status==='ordered').length,
    done:    filteredOdDeps.filter(od=>['provided','delivered'].includes(od.status)).length,
  };

  const depsForTask = taskId => ownerDeps.filter(od=>od.schedule_item_id===taskId);

  const sendReminder = async (taskIds) => {
    try {
      const res = await api.post(`/projects/${projectId}/owner-dependencies/send-reminder`, { taskIds });
      setReminderSnack({ type: 'success', msg: `Reminder sent to ${res.data.notified} person${res.data.notified===1?'':'s'} for ${res.data.sent} task${res.data.sent===1?'':'s'}` });
      setSelectedOdIds(new Set());
      setOwnerDeps(prev => prev.map(od => taskIds.includes(od.id) ? { ...od, last_reminder_sent_at: new Date().toISOString() } : od));
    } catch(e) {
      setReminderSnack({ type: 'error', msg: e.response?.data?.error || 'Failed to send reminder' });
    }
  };

  return (
    <Layout>
      <Box sx={{p:3}}>

        <Box sx={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', mb:3}}>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#1a237e">Project Schedule</Typography>
            <Typography variant="body2" color="text.secondary">Track construction tasks & owner dependencies</Typography>
          </Box>
          <FormControl size="small" sx={{minWidth:200}}>
            <InputLabel>Project</InputLabel>
            <Select value={projectId} label="Project" onChange={e=>setProjectId(e.target.value)}>
              {projects.map(p=><MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:2, mb:3}}>
          {[
            {label:'Total',       value:stats.total,        color:'#1a237e', bg:'#e8eaf6'},
            {label:'Completed',   value:stats.completed,    color:'#2e7d32', bg:'#e8f5e9'},
            {label:'In Progress', value:stats.inProgress,   color:'#1565c0', bg:'#e3f2fd'},
            {label:'Not Started', value:stats.notStarted,   color:'#757575', bg:'#f5f5f5'},
            {label:'Delayed',     value:stats.delayed,      color:'#c62828', bg:'#ffebee'},
            {label:'Progress',    value:stats.progress+'%', color:'#6a1b9a', bg:'#f3e5f5'},
          ].map(s=>(
            <Card key={s.label} elevation={0} sx={{p:2, bgcolor:s.bg, borderRadius:2, textAlign:'center'}}>
              <Typography variant="h5" fontWeight={800} color={s.color}>{s.value}</Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={500}>{s.label}</Typography>
            </Card>
          ))}
        </Box>

        <Box sx={{mb:3}}>
          <Box sx={{display:'flex', justifyContent:'space-between', alignItems:'center', mb:1}}>
            <Typography variant="h6" fontWeight={800} color="#1a237e">Overall Completion</Typography>
            <Typography variant="h5" fontWeight={800} color="#1a237e">{stats.progress}%</Typography>
          </Box>
          <LinearProgress variant="determinate" value={stats.progress}
            sx={{height:28, borderRadius:6, bgcolor:'#e0e0e0',
              '& .MuiLinearProgress-bar':{bgcolor:'#1a237e', borderRadius:6}}}/>
        </Box>

        {/* Tasks & Reminders Section */}
        <Box sx={{mb:3}}>
          <Box sx={{display:'flex', justifyContent:'space-between', alignItems:'center', mb:1.5}}>
            <Typography variant="subtitle1" fontWeight={700} color="#bf360c">
              📋 Tasks & Reminders
            </Typography>
            <Box sx={{display:'flex', alignItems:'center', gap:2}}>
              {ownerDeps.length > 0 &&
                <Typography variant="caption" color="text.secondary">{odPersonTab==='all'?ownerDeps.length:filteredOdDeps.length} total</Typography>}
              {selectedOdIds.size > 0 && (
                <Button variant="outlined" startIcon={<span>🔔</span>}
                  onClick={()=>sendReminder(Array.from(selectedOdIds))}
                  sx={{borderRadius:2, borderColor:'#bf360c', color:'#bf360c', px:2,
                    '&:hover':{bgcolor:'#fff3e0', borderColor:'#bf360c'}}}>
                  Send Reminder ({selectedOdIds.size})
                </Button>
              )}
              <Button variant="contained" startIcon={<AddIcon/>}
                onClick={()=>openOdCreate('')}
                sx={{bgcolor:'#bf360c', borderRadius:2, px:3, '&:hover':{bgcolor:'#d84315'}}}>
                Add Task
              </Button>
            </Box>
          </Box>

          {ownerDeps.length > 0 && (<>
            {/* Per-person tabs */}
            {odPersons.length > 0 && (
              <Box sx={{display:'flex', gap:1, mb:2, flexWrap:'wrap', borderBottom:'2px solid #f0f0f0', pb:1}}>
                {[{tabKey:'all', name:'All', count:ownerDeps.length}, ...odPersons.map(p=>({...p, count:ownerDeps.filter(od=>(p.tabKey==='__unassigned__'?!od.assigned_to:od.assigned_to===p.id)).length}))].map(tab=>(
                  <Box key={tab.tabKey} onClick={()=>setOdPersonTab(tab.tabKey)}
                    sx={{cursor:'pointer', px:2, py:0.75, borderRadius:2, fontSize:12, fontWeight:700,
                      bgcolor: odPersonTab===tab.tabKey ? '#bf360c' : '#f5f5f5',
                      color:   odPersonTab===tab.tabKey ? 'white'   : '#555',
                      display:'flex', alignItems:'center', gap:0.75,
                      transition:'all 0.15s',
                      '&:hover':{bgcolor: odPersonTab===tab.tabKey ? '#bf360c' : '#e0e0e0'}}}>
                    {tab.name}{tab.roleLabel ? ` (${tab.roleLabel})` : ''}
                    <Box sx={{bgcolor: odPersonTab===tab.tabKey ? 'rgba(255,255,255,0.3)' : '#ddd',
                      color: odPersonTab===tab.tabKey ? 'white' : '#777',
                      borderRadius:10, px:0.75, py:0.1, fontSize:11, fontWeight:800, minWidth:18, textAlign:'center'}}>
                      {tab.count}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
            <Box sx={{display:'flex', gap:1.5, mb:2, flexWrap:'wrap'}}>
              {[
                {label:`${odStats.overdue} Overdue`, color:'#c62828', bg:'#ffebee', show:odStats.overdue>0},
                {label:`${odStats.pending} Pending`, color:'#e65100', bg:'#fff3e0', show:odStats.pending>0},
                {label:`${odStats.ordered} Ordered`, color:'#1565c0', bg:'#e3f2fd', show:odStats.ordered>0},
                {label:`${odStats.done} Provided`,   color:'#2e7d32', bg:'#e8f5e9', show:odStats.done>0},
              ].filter(s=>s.show).map(s=>(
                <Chip key={s.label} label={s.label} size="small"
                  sx={{bgcolor:s.bg, color:s.color, fontWeight:700, fontSize:11}}/>
              ))}
            </Box>
            <Card elevation={0} sx={{border:'1px solid #cfd8dc', borderRadius:2, overflow:'hidden'}}>
              <table style={{width:'100%', borderCollapse:'collapse'}}>
                <thead>
                  <tr style={{backgroundColor:'#263238'}}>
                    {['','#','Dependency Required','Assigned To','Linked Task','Needed By','Supplier','Lead Time','Order Start','Status',''].map(h=>(
                      <th key={h} style={{padding:'8px 12px', textAlign:'left', color:'white',
                        fontWeight:600, fontSize:11, whiteSpace:'nowrap'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredOdDeps.map((od, idx) => {
                    const ds = DEP_S[od.status||'pending'];
                    const overdue = isOverdue(od);
                    const rowBg = idx%2===0?'#ffffff':'#f5f5f5';
                    const linkedTask = items.find(i=>i.id===od.schedule_item_id);
                    return (
                      <tr key={od.id} style={{borderBottom:'1px solid #e0e0e0', background:selectedOdIds.has(od.id)?'#fff8e1':undefined}}>
                        <td style={{padding:'8px 4px 8px 12px', width:28}}>
                          <input type="checkbox" checked={selectedOdIds.has(od.id)}
                            onChange={e=>{
                              const next = new Set(selectedOdIds);
                              e.target.checked ? next.add(od.id) : next.delete(od.id);
                              setSelectedOdIds(next);
                            }}
                            style={{cursor:'pointer', width:14, height:14}}/>
                        </td>
                        <td style={{padding:'8px 12px', color:'#666', fontSize:11, width:28}}>{idx+1}</td>
                        <td style={{padding:'8px 12px', backgroundColor:rowBg, minWidth:180}}>
                          <Typography variant="body2" fontWeight={700} color="#212121" sx={{fontSize:12}}>
                            {od.title}
                          </Typography>
                          {od.description &&
                            <Typography variant="caption" color="text.secondary" sx={{fontSize:10}}>
                              {od.description}
                            </Typography>}
                        </td>
                        <td style={{padding:'8px 12px', backgroundColor:rowBg, fontSize:12, color:'#555', whiteSpace:'nowrap'}}>
                          {od.assignee_first||od.assignee_last
                            ? `${od.assignee_first||''} ${od.assignee_last||''}`.trim()
                            : <span style={{color:'#bbb'}}>—</span>}
                        </td>
                        <td style={{padding:'8px 12px', backgroundColor:rowBg, fontSize:12, color:'#333'}}>
                          {linkedTask ? (
                            <Box sx={{display:'flex', alignItems:'center', gap:0.5}}>
                              <Box sx={{width:3, height:14, borderRadius:1, bgcolor:linkedTask.color||'#1a237e', flexShrink:0}}/>
                              <Typography variant="caption" sx={{fontSize:11}}>{linkedTask.title}</Typography>
                            </Box>
                          ) : <Typography variant="caption" sx={{color:'#999', fontSize:11}}>—</Typography>}
                        </td>
                        <td style={{padding:'8px 12px', backgroundColor:rowBg, whiteSpace:'nowrap', fontSize:12,
                          color:overdue?'#c62828':'#212121', fontWeight:overdue?700:500}}>
                          {fmtDate(od.needed_by)}{overdue?' ⚠️':''}
                        </td>
                        <td style={{padding:'8px 12px', backgroundColor:rowBg, fontSize:12, color:'#333'}}>{od.supplier||'—'}</td>
                        <td style={{padding:'8px 12px', backgroundColor:rowBg, fontSize:12, color:'#333', textAlign:'center'}}>
                          {od.lead_time_days?od.lead_time_days+'d':'—'}
                        </td>
                        <td style={{padding:'8px 12px', backgroundColor:rowBg, whiteSpace:'nowrap', fontSize:12, color:'#212121'}}>
                          {fmtDate(od.order_start)}
                        </td>
                        <td style={{padding:'8px 12px', backgroundColor:rowBg}}>
                          <Select size="small" value={od.status||'pending'}
                            onChange={e=>patchOdStatus(od, e.target.value, e)}
                            onClick={e=>e.stopPropagation()}
                            sx={{fontSize:11, height:24, bgcolor:ds.bg, color:ds.color, fontWeight:700,
                              '.MuiOutlinedInput-notchedOutline':{border:'none'},
                              '.MuiSelect-select':{py:0.3, px:1}}}>
                            {DEP_STATUSES.map(s=><MenuItem key={s} value={s} sx={{fontSize:11}}>{DEP_S[s].label}</MenuItem>)}
                          </Select>
                        </td>
                        <td style={{padding:'8px 12px', backgroundColor:rowBg}}>
                          <Box sx={{display:'flex', gap:0.5}}>
                            <Box onClick={()=>sendReminder([od.id])} title="Send reminder"
                            sx={{cursor:'pointer', p:0.5, borderRadius:1,
                              '&:hover':{bgcolor:'#fff8e1'}, color:'#f57f17', display:'flex', alignItems:'center', fontSize:13}}>
                            🔔
                          </Box>
                          <Box onClick={()=>openOdEdit(od)} sx={{cursor:'pointer', p:0.5, borderRadius:1,
                              '&:hover':{bgcolor:'#fff3e0'}, color:'#bf360c', display:'flex', alignItems:'center'}}>
                              <EditIcon sx={{fontSize:15}}/>
                            </Box>
                            <Box onClick={e=>removeOd(od.id, e)} sx={{cursor:'pointer', p:0.5, borderRadius:1,
                              '&:hover':{bgcolor:'#ffebee'}, color:'#c62828', display:'flex', alignItems:'center'}}>
                              <DeleteIcon sx={{fontSize:15}}/>
                            </Box>
                          </Box>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          </>)}

          {reminderSnack && (
            <Box sx={{mb:2, p:1.5, borderRadius:2,
              bgcolor:reminderSnack.type==='success'?'#e8f5e9':'#ffebee',
              color:reminderSnack.type==='success'?'#2e7d32':'#c62828',
              display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:13}}>
              {reminderSnack.msg}
              <Box onClick={()=>setReminderSnack(null)} sx={{cursor:'pointer', ml:2, fontWeight:700}}>✕</Box>
            </Box>
          )}
          {ownerDeps.length === 0 && (
            <Card elevation={0} sx={{border:'1px dashed #ffccbc', borderRadius:2, p:3, textAlign:'center'}}>
              <Typography variant="body2" color="text.secondary" sx={{mb:0.5}}>No tasks or reminders yet.</Typography>
              <Typography variant="caption" color="text.secondary">
                Add items that need action — owner selections, GC orders, supplier deadlines, etc. Assign to the responsible party.
              </Typography>
            </Card>
          )}
        </Box>

        <Box sx={{display:'flex', gap:1.5, mb:2, flexWrap:'wrap', alignItems:'center'}}>
          <Typography variant="body2" fontWeight={600} color="text.secondary">Status:</Typography>
          {['all','not_completed',...TASK_STATUSES].map(s=>(
            <Chip key={s} size="small" label={s==='all'?'All':s==='not_completed'?'Not Completed':TASK_S[s]?.label||s}
              onClick={()=>setStatusFilter(s)}
              sx={{cursor:'pointer', fontWeight:statusFilter===s?700:400,
                bgcolor:statusFilter===s?'#1a237e':'#f0f0f0',
                color:statusFilter===s?'white':'#555'}}/>
          ))}
          <Box sx={{width:1, height:20, bgcolor:'#e0e0e0', mx:0.5}}/>
          <Typography variant="body2" fontWeight={600} color="text.secondary">Type:</Typography>
          {['all',...TYPES].map(t=>(
            <Chip key={t} size="small" label={t==='all'?'All Types':t}
              onClick={()=>setTypeFilter(t)}
              sx={{cursor:'pointer', fontWeight:typeFilter===t?700:400,
                bgcolor:typeFilter===t?'#bf360c':'#f0f0f0',
                color:typeFilter===t?'white':'#555'}}/>
          ))}
          <Typography variant="caption" color="text.secondary" sx={{ml:'auto'}}>
            Showing {filtered.length} of {items.length} tasks
          </Typography>
        </Box>

        <Box sx={{display:'flex', justifyContent:'flex-end', mb:1.5}}>
          <Button variant="contained" startIcon={<AddIcon/>} onClick={openCreate}
            sx={{bgcolor:'#1a237e', borderRadius:2, px:3, '&:hover':{bgcolor:'#283593'}}}>Add Task</Button>
        </Box>

        <Card elevation={2} sx={{borderRadius:2, overflow:'hidden'}}>
          <Box sx={{overflowX:'auto'}}>
            <table style={{width:'100%', borderCollapse:'collapse'}}>
              <thead>
                <tr>
                  <SH span={8} style={{background:'#1a237e', color:'white'}}>🏗️ &nbsp; CONSTRUCTION TASK</SH>
                </tr>
                <tr>
                  <CH style={{background:'#283593', color:'white', width:36}}>#</CH>
                  <CH style={{background:'#283593', color:'white', minWidth:190}}>Task Name</CH>
                  <CH style={{background:'#283593', color:'white'}} center>Type</CH>
                  <CH style={{background:'#283593', color:'white'}}>Start</CH>
                  <CH style={{background:'#283593', color:'white'}}>End</CH>
                  <CH style={{background:'#283593', color:'white'}} center>Days</CH>
                  <CH style={{background:'#283593', color:'white', minWidth:145}}>Task Status</CH>
                  <CH style={{background:'#283593', color:'white'}} center>Actions</CH>


                </tr>
              </thead>
              <tbody>
                {filtered.map((item, idx) => {
                  const even   = idx%2===0;
                  const rowBg  = even?'#ffffff':'#f7f8ff';
                  const depBg  = even?'#fff5f2':'#ffeee8';
                  const ts     = TASK_S[item.status] || TASK_S.not_started;
                  const linked = depsForTask(item.id);
                  const hasOverdue = linked.some(isOverdue);
                  return (
                    <tr key={item.id} style={{borderBottom:'1px solid #e8eaf6'}}>
                      <TD style={{background:rowBg, color:'#bbb', fontWeight:600}}>{idx+1}</TD>
                      <TD style={{background:rowBg}}>
                        <Box sx={{display:'flex', alignItems:'center', gap:1}}>
                          <Box sx={{width:4, height:32, borderRadius:2, bgcolor:item.color||'#1a237e', flexShrink:0}}/>
                          <Box>
                            <Typography variant="body2" fontWeight={700} sx={{lineHeight:1.3}}>{item.title}</Typography>
                            {item.description &&
                              <Typography variant="caption" color="text.secondary" sx={{fontSize:10}}>
                                {item.description}
                              </Typography>}
                          </Box>
                        </Box>
                      </TD>
                      <TD center style={{background:rowBg}}>
                        <Chip label={item.type} size="small"
                          sx={{fontSize:10, height:20, fontWeight:600,
                            bgcolor:(item.color||'#1a237e')+'22', color:item.color||'#1a237e'}}/>
                      </TD>
                      <TD style={{background:rowBg, color:'#444', whiteSpace:'nowrap'}}>{fmtDate(item.start_date)}</TD>
                      <TD style={{background:rowBg, color:'#444', whiteSpace:'nowrap'}}>{fmtDate(item.end_date)}</TD>
                      <TD center style={{background:rowBg, color:'#666'}}>{days(item.start_date, item.end_date)}</TD>
                      <TD style={{background:rowBg}}>
                        <Box sx={{display:'flex', flexDirection:'column', gap:0.5}}>
                          <StatusSelect value={item.status} options={TASK_STATUSES} map={TASK_S}
                            onChange={v=>patchItem(item,{status:v})}/>
                          <Box sx={{display:'flex', alignItems:'center', gap:0.5}}>
                            <LinearProgress variant="determinate" value={item.progress||0}
                              sx={{flex:1, height:4, borderRadius:2, bgcolor:'#e0e0e0',
                                '& .MuiLinearProgress-bar':{bgcolor:ts.color}}}/>
                            <Typography variant="caption" sx={{color:ts.color, fontSize:10, minWidth:26}}>
                              {item.progress||0}%
                            </Typography>
                          </Box>
                        </Box>
                      </TD>
                      <TD center style={{background:rowBg}}>
                        <Box sx={{display:'flex', gap:0.5, justifyContent:'center'}}>
                          <Box onClick={()=>openEdit(item)} sx={{cursor:'pointer', p:0.5, borderRadius:1,
                            '&:hover':{bgcolor:'#e8eaf6'}, color:'#1a237e', display:'flex', alignItems:'center'}}>
                            <EditIcon sx={{fontSize:16}}/>
                          </Box>
                          <Box onClick={()=>remove(item.id)} sx={{cursor:'pointer', p:0.5, borderRadius:1,
                            '&:hover':{bgcolor:'#ffebee'}, color:'#c62828', display:'flex', alignItems:'center'}}>
                            <DeleteIcon sx={{fontSize:16}}/>
                          </Box>
                        </Box>
                      </TD>


                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length===0 && (
              <Box sx={{textAlign:'center', py:8, color:'text.secondary'}}>
                <Typography>No tasks found. Add your first task!</Typography>
              </Box>
            )}
          </Box>
        </Card>

        {/* Side Panel */}
        {panel && (
          <Box sx={{position:'fixed', top:0, right:0, width:440, height:'100vh', bgcolor:'white',
            boxShadow:'-4px 0 24px rgba(0,0,0,0.15)', zIndex:1300, overflowY:'auto',
            display:'flex', flexDirection:'column'}}>
            <Box sx={{bgcolor:'#1a237e', color:'white', p:2.5}}>
              <Box sx={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                <Typography variant="h6" fontWeight={700} sx={{fontSize:16}}>{panel.title}</Typography>
                <Box onClick={()=>{setPanel(null); setPanelOdEdit(false);}}
                  sx={{cursor:'pointer', fontSize:20, opacity:0.8, '&:hover':{opacity:1}}}>✕</Box>
              </Box>
              <Box sx={{display:'flex', gap:1, mt:1}}>
                <Chip label={panel.type} size="small" sx={{bgcolor:'rgba(255,255,255,0.2)', color:'white', fontSize:10}}/>
                <Chip label={TASK_S[panel.status]?.label||panel.status} size="small"
                  sx={{bgcolor:TASK_S[panel.status]?.bg, color:TASK_S[panel.status]?.color, fontSize:10, fontWeight:700}}/>
              </Box>
            </Box>
            <Box sx={{p:2.5, flex:1}}>
              <Typography variant="subtitle2" fontWeight={700} color="#1a237e" sx={{mb:1.5, letterSpacing:0.5}}>
                🏗️ CONSTRUCTION TASK
              </Typography>
              <Box sx={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:1.5, mb:2}}>
                {[
                  {label:'Start Date', value:fmtDate(panel.start_date)},
                  {label:'End Date',   value:fmtDate(panel.end_date)},
                  {label:'Duration',   value:days(panel.start_date, panel.end_date)},
                  {label:'Type',       value:panel.type},
                ].map(f=>(
                  <Box key={f.label} sx={{bgcolor:'#f5f6fa', borderRadius:1.5, p:1.5}}>
                    <Typography variant="caption" color="text.secondary" display="block">{f.label}</Typography>
                    <Typography variant="body2" fontWeight={600}>{f.value||'—'}</Typography>
                  </Box>
                ))}
              </Box>
              {panel.description && (
                <Box sx={{bgcolor:'#f5f6fa', borderRadius:1.5, p:1.5, mb:2}}>
                  <Typography variant="caption" color="text.secondary">Description</Typography>
                  <Typography variant="body2">{panel.description}</Typography>
                </Box>
              )}
              <Box sx={{bgcolor:'#f5f6fa', borderRadius:1.5, p:1.5, mb:2}}>
                <Box sx={{display:'flex', justifyContent:'space-between', mb:0.5}}>
                  <Typography variant="caption" color="text.secondary">Progress</Typography>
                  <Typography variant="caption" fontWeight={700} color="#1a237e">{panel.progress||0}%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={panel.progress||0}
                  sx={{height:8, borderRadius:4, bgcolor:'#e0e0e0',
                    '& .MuiLinearProgress-bar':{bgcolor:TASK_S[panel.status]?.color||'#1a237e'}}}/>
              </Box>
              <Box sx={{mb:3}}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{mb:0.5}}>Task Status</Typography>
                <StatusSelect value={panel.status} options={TASK_STATUSES} map={TASK_S}
                  onChange={v=>{ patchItem(panel,{status:v}); setPanel({...panel, status:v}); }}/>
              </Box>


            </Box>
            <Box sx={{p:2, borderTop:'1px solid #e0e0e0', display:'flex', gap:1}}>
              <Button fullWidth variant="outlined" onClick={()=>{setPanel(null); openEdit(panel);}}
                sx={{borderRadius:2, borderColor:'#1a237e', color:'#1a237e'}}>✏️ Edit Task</Button>
              <Button fullWidth variant="outlined" color="error"
                onClick={()=>{ remove(panel.id); setPanel(null); }}
                sx={{borderRadius:2}}>🗑️ Delete</Button>
            </Box>
          </Box>
        )}
        {panel && (
          <Box onClick={()=>{setPanel(null); setPanelOdEdit(false);}}
            sx={{position:'fixed', top:0, left:0, width:'100vw', height:'100vh',
              bgcolor:'rgba(0,0,0,0.3)', zIndex:1299}}/>
        )}




        {/* Owner Dependency Dialog */}
        <Dialog open={odDialog} onClose={()=>setOdDialog(false)} maxWidth="sm" fullWidth
          PaperProps={{sx:{borderRadius:3}}}>
          <DialogTitle sx={{fontWeight:800, color:'#bf360c', borderBottom:'1px solid #e0e0e0', pb:2}}>
            {odEdit ? '✏️ Edit Task / Reminder' : '📋 Add Task / Reminder'}
          </DialogTitle>
          <DialogContent sx={{pt:2.5}}>
            {odError && <Alert severity="error" sx={{mb:2}}>{odError}</Alert>}
            <Box sx={{display:'flex', flexDirection:'column', gap:2}}>
              <TextField label="Task or reminder description *" value={odForm.title}
                onChange={e=>setOdForm({...odForm, title:e.target.value})}
                fullWidth placeholder="e.g. Paint color selection, Order windows, Confirm permit"/>
              <TextField select label="Assign To" value={odForm.assigned_to||''}
                onChange={e=>setOdForm({...odForm, assigned_to:e.target.value})} fullWidth>
                <MenuItem value="">— Assign to a party —</MenuItem>
                {projectMembers.map(m=>(
                  <MenuItem key={m.id} value={m.id}>
                    {m.name} ({m.role==='homeowner'?'Owner':'GC'})
                  </MenuItem>
                ))}
              </TextField>
              <TextField label="Description (optional)" value={odForm.description}
                onChange={e=>setOdForm({...odForm, description:e.target.value})}
                fullWidth multiline rows={2} placeholder="Additional details or context"/>
              <TextField select label="Linked Task (optional)" value={odForm.schedule_item_id||''}
                onChange={e=>setOdForm({...odForm, schedule_item_id:e.target.value})} fullWidth>
                <MenuItem value="">— No linked task —</MenuItem>
                {items.map(i=>(
                  <MenuItem key={i.id} value={i.id}>
                    <Box sx={{display:'flex', alignItems:'center', gap:1}}>
                      <Box sx={{width:8, height:8, borderRadius:'50%', bgcolor:i.color||'#1a237e'}}/>
                      {i.title}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
              <Box sx={{display:'flex', gap:2}}>
                <TextField label="Needed By" type="date" value={odForm.needed_by}
                  onChange={e=>setOdForm({...odForm, needed_by:e.target.value})}
                  fullWidth InputLabelProps={{shrink:true}}/>
                <TextField select label="Status" value={odForm.status}
                  onChange={e=>setOdForm({...odForm, status:e.target.value})} fullWidth>
                  {DEP_STATUSES.map(s=><MenuItem key={s} value={s}>{DEP_S[s].label}</MenuItem>)}
                </TextField>
              </Box>
              <Box sx={{display:'flex', gap:2}}>
                <TextField label="Supplier" value={odForm.supplier}
                  onChange={e=>setOdForm({...odForm, supplier:e.target.value})}
                  fullWidth placeholder="e.g. Andersen Windows, Home Depot"/>
                <TextField label="Lead Time (days)" type="number" value={odForm.lead_time_days}
                  onChange={e=>setOdForm({...odForm, lead_time_days:e.target.value})} fullWidth/>
              </Box>
              <TextField label="Order Start Date" type="date" value={odForm.order_start}
                onChange={e=>setOdForm({...odForm, order_start:e.target.value})}
                fullWidth InputLabelProps={{shrink:true}}/>
            </Box>
          </DialogContent>
          <DialogActions sx={{p:3, borderTop:'1px solid #e0e0e0', gap:1}}>
            <Button onClick={()=>setOdDialog(false)} sx={{borderRadius:2}}>Cancel</Button>
            <Button variant="contained" onClick={saveOd} disabled={odSaving||!odForm.title}
              sx={{bgcolor:'#bf360c', borderRadius:2, px:3}}>
              {odSaving ? 'Saving…' : odEdit ? 'Update Task' : 'Add Task'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Task Dialog */}
        <Dialog open={dialog} onClose={()=>setDialog(false)} maxWidth="sm" fullWidth
          PaperProps={{sx:{borderRadius:3}}}>
          <DialogTitle sx={{fontWeight:800, color:'#1a237e', borderBottom:'1px solid #e0e0e0', pb:2}}>
            {editItem ? '✏️ Edit Task' : '➕ New Schedule Task'}
          </DialogTitle>
          <DialogContent sx={{pt:2.5}}>
            {error && <Alert severity="error" sx={{mb:2}}>{error}</Alert>}
            <Typography variant="subtitle2" fontWeight={700} color="#1a237e" sx={{mb:1.5}}>
              🏗️ TASK DETAILS
            </Typography>
            <Box sx={{display:'flex', flexDirection:'column', gap:2}}>
              <Box sx={{display:'flex', gap:2}}>
                <TextField select label="Category" value={taskCategory}
                  onChange={e=>setTaskCategory(e.target.value)} fullWidth>
                  <MenuItem value="">— Select Category —</MenuItem>
                  {TASK_CATEGORIES.map(c=><MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
                <TextField select label="Task Name *" value={form.title}
                  onChange={e=>{
                    const t = STANDARD_TASKS.find(x=>x.label===e.target.value);
                    if(t && t.label!=='Custom Task'){
                      setForm({...form, title:e.target.value, type:t.type, color:t.color});
                    } else {
                      setForm({...form, title:t?.label==='Custom Task'?'':e.target.value});
                    }
                  }} fullWidth>
                  <MenuItem value="">— Select Task —</MenuItem>
                  {STANDARD_TASKS.filter(t=>!taskCategory||t.category===taskCategory).map(t=>(
                    <MenuItem key={t.label} value={t.label}>{t.label}</MenuItem>
                  ))}
                </TextField>
              </Box>
              {(form.title===''||form.title==='Custom Task') && (
                <TextField label="Custom Task Name *" value={form.title==='Custom Task'?'':form.title}
                  onChange={e=>setForm({...form, title:e.target.value})}
                  fullWidth placeholder="Enter custom task name"/>
              )}
              <TextField label="Description" multiline rows={2} value={form.description}
                onChange={e=>setForm({...form, description:e.target.value})} fullWidth/>
              <Box sx={{display:'flex', gap:2}}>
                <TextField select label="Type" value={form.type}
                  onChange={e=>setForm({...form, type:e.target.value})} fullWidth>
                  {TYPES.map(t=><MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
                <TextField select label="Status" value={form.status}
                  onChange={e=>setForm({...form, status:e.target.value})} fullWidth>
                  {TASK_STATUSES.map(s=><MenuItem key={s} value={s}>{TASK_S[s].label}</MenuItem>)}
                </TextField>
              </Box>
              <Box sx={{display:'flex', gap:2}}>
                <TextField label="Start Date *" type="date" value={form.start_date}
                  onChange={e=>setForm({...form, start_date:e.target.value})}
                  fullWidth InputLabelProps={{shrink:true}}/>
                <TextField label="End Date *" type="date" value={form.end_date}
                  onChange={e=>setForm({...form, end_date:e.target.value})}
                  fullWidth InputLabelProps={{shrink:true}}/>
              </Box>
              <Box sx={{display:'flex', gap:2, alignItems:'center'}}>
                <TextField label="Progress (%)" type="number" value={form.progress}
                  onChange={e=>setForm({...form, progress:Math.min(100,Math.max(0,+e.target.value||0))})}
                  fullWidth inputProps={{min:0, max:100}}/>
                <Box sx={{display:'flex', alignItems:'center', gap:1.5}}>
                  <Typography variant="body2" color="text.secondary">Color</Typography>
                  <input type="color" value={form.color}
                    onChange={e=>setForm({...form, color:e.target.value})}
                    style={{width:44, height:36, border:'1px solid #ddd', borderRadius:6, cursor:'pointer'}}/>
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{p:3, borderTop:'1px solid #e0e0e0', gap:1}}>
            <Button onClick={()=>setDialog(false)} sx={{borderRadius:2}}>Cancel</Button>
            <Button variant="contained" onClick={save} disabled={saving}
              sx={{bgcolor:'#1a237e', borderRadius:2, px:3}}>
              {saving ? 'Saving…' : editItem ? 'Update Task' : 'Create Task'}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Layout>
  );
}
